import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AssignCategoriesRequest,
  SupplierCategoryAssignmentResponse,
} from "../types";
import { queryKeys } from "./queryKeys";
import axios from "../axios-orders";

export const getAssignedCategories = async (): Promise<
  SupplierCategoryAssignmentResponse[]
> => {
  const { data } = await axios.get(
    "/api/supplier/ai-profile/assigned-categories"
  );
  return data;
};

export const useAssignedCategories = () => {
  return useQuery<SupplierCategoryAssignmentResponse[]>({
    queryKey: [queryKeys.SUPPLIER_ASSIGNED_CATEGORIES],
    queryFn: getAssignedCategories,
  });
};

// Every query whose result depends on the seller's category assignments — invalidated after any
// assign/remove so the assigned list, the home banners AND the opportunity dashboard (counts /
// filters / per-type lists) all reflect the change immediately.
export const CATEGORY_ASSIGNMENT_QUERY_KEYS = [
  queryKeys.SUPPLIER_ASSIGNED_CATEGORIES,
  queryKeys.USER_MATERIAL_CATEGORIES,
  queryKeys.OPPORTUNITY_COUNTS,
  queryKeys.OPPORTUNITY_TOTAL_COUNT,
  queryKeys.OPPORTUNITY_FILTERS,
  queryKeys.OPPORTUNITY_FILTERS_ALL,
  queryKeys.OPPORTUNITY_LIST,
];

/**
 * Assigns one or more material categories to the current seller
 * (`POST /api/supplier/ai-profile/assign-manual-categories`, source MANUAL — an idempotent upsert
 * that reactivates an existing inactive assignment or creates a new one). The manual counterpart to
 * `useRemoveUserMaterialCategory`; both invalidate the same query set so the dashboard stays in sync.
 */
export const useAssignManualCategories = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryIds: string[]) => {
      const body: AssignCategoriesRequest = { categoryIds };
      const { data } = await axios.post(
        "/api/supplier/ai-profile/assign-manual-categories",
        body
      );
      return data;
    },
    onSuccess: () => {
      CATEGORY_ASSIGNMENT_QUERY_KEYS.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
      onSuccess?.();
    },
    onError,
  });
};
